import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  TextField,
  MenuItem,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Button
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { usePermission } from "../../../permissions/usePermission";
import dummyImage from "../../../images/user.png";
import { getPublicS3Url } from "../../../utils/s3PublicUrl";
import { setNetworkRestoreOnce } from "./networkStateKeys";
import { exportNetworkMembersCsv } from "./networkExport";

const COMMITTEE_PRIORITY = [
  "Operation Committee",
  "Program Head",
  "Other Members",
];

const COMMITTEE_MAP = {
  "Operational commitee": "Operation Committee",
  "Operations Committee": "Operation Committee",
  "Operation Committee": "Operation Committee",
  "National Program Head": "Program Head",
  "Program Head": "Program Head",
  "Other Member": "Other Members",
  "Other Members": "Other Members",
};

const getValueName = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object") {
    return String(value?.name || value?.title || value?.code || "").trim();
  }
  return String(value).trim();
};

const getFullName = (member) =>
  [member?.salutation || member?.Salutation, member?.userFirstName, member?.userMiddleName, member?.userLastName]
    .filter(Boolean)
    .join(" ");

const getMemberYear = (member) => {
  const rawDate = member?.joiningDate || member?.updatedAt;
  if (!rawDate) return "";

  const date = new Date(rawDate);
  return Number.isNaN(date.getTime()) ? "" : String(date.getFullYear());
};

const extractStringId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (typeof value.$oid === "string") return value.$oid;
    if (value._id) return extractStringId(value._id);
  }
  return "";
};

const getMemberProfileUserId = (member) =>
  extractStringId(member?.userId) ||
  extractStringId(member?.user?._id) ||
  extractStringId(member?.user) ||
  extractStringId(member?._id);

const getLatestUpdatedAt = (members = []) =>
  members.reduce((acc, member) => {
    const ts = member?.updatedAt || member?.joiningDate;
    if (!ts) return acc;

    const date = new Date(ts);
    if (Number.isNaN(date.getTime())) {
      return acc;
    }

    return !acc || date > acc ? date : acc;
  }, null);

const formatUpdatedDate = (date) => {
  if (!date) return "";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const DEC_FILTERS_STORAGE_KEY = "network_filters_dec";

const getCommitteeLabel = (member) => {
  const committeeType = getValueName(member?.committeeType);
  const committeeName = getValueName(member?.committee);
  return COMMITTEE_MAP[committeeName] || COMMITTEE_MAP[committeeType] || committeeName || committeeType;
};

const getMemberType = (member) => {
  const committeeSubtype = String(
    member?.committeeSubtype ||
    member?.committeeSubType ||
    member?.committee?.committeeSubtype ||
    member?.committee?.committeeSubType ||
    member?.committeeType?.committeeSubtype ||
    member?.committeeType?.committeeSubType ||
    ""
  ).trim();
  if (committeeSubtype && committeeSubtype.toLowerCase() !== "na") {
    return committeeSubtype;
  }

  return getValueName(member?.committeeType);
};

const DEC = ({ members = [], allowScopedRestore = false, canOpenMemberProfile = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const { canByKey } = usePermission();
  const canDownloadExcel = canByKey(
    "COMMITTEE_MANAGEMENT_COMMITTEE_MEMBERS_DOWNLOAD_EXCEL",
  );
  const navigateFrom = location.pathname.includes("committeeMembersList")
    ? "committeeMembersList"
    : "network";

  const [selectedState, setSelectedState] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedCommittee, setSelectedCommittee] = useState("ALL");
  const [selectedType, setSelectedType] = useState("ALL");
  const [selectedYear, setSelectedYear] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    if (!allowScopedRestore) return;
    try {
      const saved = sessionStorage.getItem(DEC_FILTERS_STORAGE_KEY);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      setSelectedState(parsed?.selectedState || "");
      setSelectedRegion(parsed?.selectedRegion || "");
      setSelectedDistrict(parsed?.selectedDistrict || "");
      setSelectedCommittee(parsed?.selectedCommittee || "ALL");
      setSelectedType(parsed?.selectedType || "ALL");
      setSelectedYear(parsed?.selectedYear || "ALL");
      setSearchTerm(parsed?.searchTerm || "");
      setShowSearch(Boolean(parsed?.showSearch));
    } catch (error) {
      console.warn("Unable to restore DEC filters", error);
    }
  }, [allowScopedRestore]);

  useEffect(() => {
    sessionStorage.setItem(
      DEC_FILTERS_STORAGE_KEY,
      JSON.stringify({
        selectedState,
        selectedRegion,
        selectedDistrict,
        selectedCommittee,
        selectedType,
        selectedYear,
        searchTerm,
        showSearch,
      }),
    );
  }, [
    selectedState,
    selectedRegion,
    selectedDistrict,
    selectedCommittee,
    selectedType,
    selectedYear,
    searchTerm,
    showSearch,
  ]);

  const states = useMemo(
    () =>
      [...new Set(members.map((item) => getValueName(item?.state)).filter(Boolean))].sort(),
    [members]
  );

  const regions = useMemo(
    () =>
      [...new Set(
        members
          .filter(
            (item) =>
              !selectedState || getValueName(item?.state) === selectedState
          )
          .map((item) => getValueName(item?.region))
          .filter(Boolean)
      )].sort(),
    [members, selectedState]
  );

  const districts = useMemo(
    () =>
      [...new Set(
        members
          .filter(
            (item) =>
              (!selectedState || getValueName(item?.state) === selectedState) &&
              (!selectedRegion || getValueName(item?.region) === selectedRegion)
          )
          .map((item) => getValueName(item?.district))
          .filter(Boolean)
      )].sort(),
    [members, selectedState, selectedRegion]
  );

  const locationScopedMembers = useMemo(
    () =>
      members.filter(
        (item) =>
          (!selectedState || getValueName(item?.state) === selectedState) &&
          (!selectedRegion || getValueName(item?.region) === selectedRegion) &&
          (!selectedDistrict || getValueName(item?.district) === selectedDistrict)
      ),
    [members, selectedState, selectedRegion, selectedDistrict]
  );

  const hasLocationData = locationScopedMembers.length > 0;
  const disableAdvancedFilters = !hasLocationData;
  const isTypeFilterReady =
    Boolean(selectedState) &&
    Boolean(selectedRegion) &&
    Boolean(selectedDistrict) &&
    selectedCommittee !== "ALL";

  const committees = useMemo(
    () =>
      [...new Set(locationScopedMembers.map(getCommitteeLabel).filter(Boolean))].sort(),
    [locationScopedMembers]
  );

  const years = useMemo(
    () =>
      [...new Set(locationScopedMembers.map(getMemberYear).filter(Boolean))].sort(
        (a, b) => Number(b) - Number(a)
      ),
    [locationScopedMembers]
  );

  const types = useMemo(
    () =>
      [
        ...new Set(
          locationScopedMembers
            .filter(
              (item) =>
                selectedCommittee === "ALL" ||
                getCommitteeLabel(item) === selectedCommittee
            )
            .map(getMemberType)
            .filter(Boolean)
        ),
      ].sort(),
    [locationScopedMembers, selectedCommittee]
  );

  const filteredMembers = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();

    return locationScopedMembers.filter((item) => {
      const committeeLabel = getCommitteeLabel(item);
      const memberType = getMemberType(item);
      const memberYear = getMemberYear(item);
      const fullName = getFullName(item).toLowerCase();

      const matchCommittee =
        selectedCommittee === "ALL" || committeeLabel === selectedCommittee;
      const matchType = selectedType === "ALL" || memberType === selectedType;
      const matchYear = selectedYear === "ALL" || memberYear === selectedYear;
      const matchSearch = fullName.includes(lowerSearch);

      return matchCommittee && matchType && matchYear && matchSearch;
    });
  }, [
    locationScopedMembers,
    searchTerm,
    selectedCommittee,
    selectedType,
    selectedYear,
  ]);
  const hasFilteredMembers = filteredMembers.length > 0;

  const committeeOrder = useMemo(() => {
    const dynamicCommittees = [...new Set(filteredMembers.map(getCommitteeLabel).filter(Boolean))];
    const extras = dynamicCommittees.filter(
      (committee) => !COMMITTEE_PRIORITY.includes(committee)
    );
    return [...COMMITTEE_PRIORITY, ...extras];
  }, [filteredMembers]);

  const groupedMembers = useMemo(() => {
    const grouped = committeeOrder.reduce((acc, committee) => {
      acc[committee] = [];
      return acc;
    }, {});

    filteredMembers.forEach((item) => {
      const committee = getCommitteeLabel(item);
      if (!committee) return;
      if (!grouped[committee]) grouped[committee] = [];
      grouped[committee].push(item);
    });

    return grouped;
  }, [filteredMembers, committeeOrder]);

  const visibleUpdatedAt = useMemo(
    () => getLatestUpdatedAt(filteredMembers),
    [filteredMembers]
  );

  const getCommitteeStyle = (committee) =>
    theme.committeeStyles?.[committee] || theme.committeeStyles?.default;

  const handleExport = () => {
    exportNetworkMembersCsv(filteredMembers, "network_dec_members");
  };

  return (
    <Box sx={{ px: { xs: 1, sm: 3 }, mt: 3 }}>
      <Box
        sx={{
          //   borderRadius: 4,
          //   px: isMobile ? 1 : 2,
          mt: 6
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              xl: "row",
            },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gap: 1.5,

              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "repeat(3, 1fr)",
                xl: "repeat(6, minmax(180px, 200px))",
              },

              width: "100%",
            }}
          >
            <TextField
              select
              size="small"
              label="State"
              value={selectedState}
              onChange={(e) => {
                setSelectedState(e.target.value);
                setSelectedRegion("");
                setSelectedDistrict("");
                setSelectedCommittee("ALL");
                setSelectedType("ALL");
                setSelectedYear("ALL");
                setSearchTerm("");
                setShowSearch(false);
              }}
              sx={{ width: { xs: "100%", xl: 200 } }}
            >
              <MenuItem value="">Select State</MenuItem>
              {states.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Region"
              value={selectedRegion}
              onChange={(e) => {
                setSelectedRegion(e.target.value);
                setSelectedDistrict("");
                setSelectedCommittee("ALL");
                setSelectedType("ALL");
                setSelectedYear("ALL");
                setSearchTerm("");
                setShowSearch(false);
              }}
              disabled={!selectedState}
              sx={{ width: { xs: "100%", xl: 200 } }}
            >
              <MenuItem value="">Select Region</MenuItem>
              {regions.map((region) => (
                <MenuItem key={region} value={region}>
                  {region}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="District"
              value={selectedDistrict}
              onChange={(e) => {
                setSelectedDistrict(e.target.value);
                setSelectedCommittee("ALL");
                setSelectedType("ALL");
                setSelectedYear("ALL");
                setSearchTerm("");
                setShowSearch(false);
              }}
              disabled={!selectedRegion}
              sx={{ width: { xs: "100%", xl: 200 } }}
            >
              <MenuItem value="">Select District</MenuItem>
              {districts.map((district) => (
                <MenuItem key={district} value={district}>
                  {district}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Committee"
              value={selectedCommittee}
              onChange={(e) => {
                setSelectedCommittee(e.target.value);
                setSelectedType("ALL");
              }}
              disabled={disableAdvancedFilters}
              sx={{ width: { xs: "100%", xl: 200 } }}
            >
              <MenuItem value="ALL">All Committees</MenuItem>
              {committees.map((committee) => (
                <MenuItem key={committee} value={committee}>
                  {committee}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Type"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              disabled={!isTypeFilterReady || disableAdvancedFilters}
              sx={{ width: { xs: "100%", xl: 200 } }}
            >
              <MenuItem value="ALL">All Types</MenuItem>
              {types.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Year"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              disabled={disableAdvancedFilters}
              sx={{ width: { xs: "100%", xl: 200 } }}
            >
              <MenuItem value="ALL">All Years</MenuItem>
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box
            sx={{
              // position: isMobile ? "static" : "absolute",
              // right: 0,
              width: { xs: "100%", xl: "auto" },
              display: "flex",
              alignItems: "center",
              gap: 1,
              justifyContent: "flex-end",
            }}
          >


            {canDownloadExcel && (
              <Tooltip title="Download Excel" arrow>
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<FileDownloadOutlinedIcon />}
                    onClick={handleExport}
                    disabled={!filteredMembers.length}
                    aria-label="Download Excel"
                    sx={{
                      textTransform: "none",
                      //borderRadius: 2,
                    }}
                  >
                    Download
                  </Button>
                </span>
              </Tooltip>
            )}
            {showSearch ? (
              <TextField
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                disabled={disableAdvancedFilters}
                sx={{ width: isMobile ? "100%" : 220 }}
              />
            ) : (
              <IconButton
                onClick={() => setShowSearch(true)}
                disabled={disableAdvancedFilters}
              >
                <SearchIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      {!hasLocationData && (
        <Box
          sx={{
            px: 2,
            py: 4,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            No DEC members found for the selected filters
          </Typography>
        </Box>
      )}

      {hasLocationData && !hasFilteredMembers && (
        <Box
          sx={{
            px: 2,
            py: 4,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            No DEC members found for the selected filters
          </Typography>
        </Box>
      )}

      {committeeOrder.map((committee) => {
        const membersList = groupedMembers[committee];
        if (!membersList || membersList.length === 0) return null;

        const style = getCommitteeStyle(committee);

        return (
          <Box
            key={committee}
            sx={{
              borderRadius: 4,
              mb: 4,
              px: 2,
              py: 2,
              background: style?.gradient,
            }}
          >
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography variant="h5" fontWeight={600}>
                {committee}
              </Typography>
            </Box>

            <Grid container spacing={3} justifyContent="center">
              {membersList.map((member) => (
                <Grid item xs={12} sm={6} md={4} key={member._id || getFullName(member)}>
                  <Card
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 3,
                      borderRadius: 3,
                      border: "1px solid #e6e6e6",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      cursor: canOpenMemberProfile ? "pointer" : "default",
                      "&:hover": canOpenMemberProfile
                        ? {
                          boxShadow: "0 6px 16px rgba(0,0,0,0.1)",
                        }
                        : {},
                    }}
                    onClick={() => {
                      const profileUserId = getMemberProfileUserId(member);
                      if (canOpenMemberProfile && profileUserId) {
                        setNetworkRestoreOnce();
                        navigate(`/profile/${profileUserId}`, {
                          state: {
                            mode: "view",
                            navigateFrom,
                            canOpenMemberProfile,
                          },
                        });
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: 2,
                        overflow: "hidden",
                        background: style?.solid,
                        mr: 2,
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        component="img"
                        src={getPublicS3Url(member?.userImageURL) || dummyImage}
                        alt={getFullName(member)}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography fontWeight={700}>{getFullName(member)}</Typography>

                      <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
                        {member?.position || ""}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}

      {visibleUpdatedAt && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Last updated: {formatUpdatedDate(visibleUpdatedAt)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DEC;
